import isNotNull from '@lib/utilities/object/isNotNull'
import pluralize from '@lib/utilities/text/pluralize'
import { isOtsukaMdd } from '@modules/trials/helpers/isHardcodedTrial'
import { compareOutcomeTimeframes } from '@modules/trials/helpers/parseMeasurementsFromTrial'
import { type TrialAssociation } from '@modules/trials/types/TrialAssociation'
import { TrialOverridesTypeValidator } from '@modules/trials/types/TrialOverrides'

export type ParsedTimelineAttribute = {
  duration: string
  visitCount: string | null
}

export const getTimelineValues = (trial: TrialAssociation) => {
  const [finalMeasurement] = trial.outcomes
    .sort(compareOutcomeTimeframes)
    .reverse()

  const trialOverridesResult = TrialOverridesTypeValidator.safeParse(
    trial.overrides,
  )
  const overrides = trialOverridesResult.success
    ? trialOverridesResult.data
    : undefined
  const timeline = trial.timeline

  const screening = (): ParsedTimelineAttribute => {
    // 0 is valid even though its falsey
    if (isNotNull(timeline?.screeningDuration)) {
      return {
        duration: `${pluralize(
          timeline.screeningDurationUnit?.toLowerCase() ?? 'day',
          timeline.screeningDuration ?? 0,
          { includeCount: true },
        )}`,
        visitCount: `${pluralize('visit', timeline.screeningVisitCount ?? 0, {
          includeCount: true,
        })}`,
      }
    } else if (overrides?.timeline?.screeningDuration) {
      return {
        duration: overrides.timeline.screeningDuration,
        visitCount: null,
      }
    } else {
      return {
        duration: '3 weeks',
        visitCount: '3 visits',
      }
    }
  }

  const treatment = (): ParsedTimelineAttribute => {
    if (
      isNotNull(timeline?.treatmentDuration) &&
      timeline?.treatmentDurationUnit
    ) {
      return {
        duration: `${pluralize(
          timeline.treatmentDurationUnit?.toLowerCase() ?? 'month',
          timeline.treatmentDuration ?? 0,
          { includeCount: true },
        )}`,
        visitCount: customTreatmentVisitCount({
          nctId: trial.nctId,
          treatmentVisitCount: timeline.treatmentVisitCount,
        }),
      }
    } else {
      return {
        duration: 'Varies',
        visitCount: null,
      }
    }
  }

  const reporting = (): ParsedTimelineAttribute => {
    if (isNotNull(timeline?.followupDuration)) {
      return {
        duration: `${pluralize(
          timeline.followupDurationUnit?.toLowerCase() ?? 'day',
          timeline.followupDuration ?? 0,
          { includeCount: true },
        )}`,
        visitCount: customFollowUpVisitCount({
          followupVisitCount: timeline.followupVisitCount,
          nctId: trial.nctId,
        }),
      }
    } else if (finalMeasurement?.timeframe) {
      return {
        duration: finalMeasurement?.timeframe.toLowerCase(),
        visitCount: null,
      }
    } else {
      return {
        duration: 'up to 5 years',
        visitCount: null,
      }
    }
  }

  return {
    reporting: reporting(),
    screening: screening(),
    treatment: treatment(),
  }
}

const customTreatmentVisitCount = ({
  nctId,
  treatmentVisitCount,
}: {
  nctId: TrialAssociation['nctId']
  treatmentVisitCount: NonNullable<
    TrialAssociation['timeline']
  >['treatmentVisitCount']
}) => {
  const count = treatmentVisitCount ?? 0
  if (isOtsukaMdd(nctId)) {
    // up to 13 weeks
    return `up to ${pluralize('visit', count, { includeCount: true })}`
  }
  return `${pluralize('visit', count, { includeCount: true })}`
}

const customFollowUpVisitCount = ({
  followupVisitCount,
  nctId,
}: {
  followupVisitCount: NonNullable<
    TrialAssociation['timeline']
  >['followupVisitCount']
  nctId: TrialAssociation['nctId']
}) => {
  const count = followupVisitCount ?? 0
  if (isOtsukaMdd(nctId)) {
    // 1 call or visit
    return `${pluralize('call', count, {
      includeCount: true,
    })} or ${pluralize('visit', count, {
      includeCount: false,
    })}`
  }
  return `${pluralize('visit', count, { includeCount: true })}`
}
